var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'jsx-dom';
import { attr, listen, memoize, prop } from '@exadel/esl/modules/esl-utils/decorators';
import { ESLIntersectionTarget } from '@exadel/esl/modules/esl-event-listener/core';
import { parseBoolean, toBooleanAttribute } from '@exadel/esl/modules/esl-utils/misc';
import { promisifyEvent, promisifyNextRender, promisifyTimeout } from '@exadel/esl/modules/esl-utils/async';
import { UIPPlugin } from '../base/plugin';
import { UIPRenderingTemplatesService } from '../processors/templates';
import { UIPJSRenderingPreprocessors, UIPHTMLRenderingPreprocessors } from '../processors/rendering';
/**
 * Preview {@link UIPPlugin} custom element definition.
 * Mandatory for UI Playground rendering. Displays active playground content
 */
export class UIPPreview extends UIPPlugin {
    constructor() {
        super(...arguments);
        this._iframeResizeRAF = 0;
    }
    /** {@link UIPPlugin} section wrapper */
    get $inner() {
        const pluginType = this.constructor;
        return React.createElement("div", { className: `${pluginType.is}-inner uip-plugin-inner esl-scrollable-content` });
    }
    get $iframe() {
        return React.createElement("iframe", { className: "uip-preview-iframe", frameBorder: "0" });
    }
    /** Extra element to animate decreasing height of content smoothly */
    get $container() {
        const type = this.constructor;
        return (React.createElement("div", { class: `uip-plugin-content ${type.is}-container` },
            React.createElement("esl-scrollbar", { class: type.is + '-v-scroll', target: "::next(.uip-plugin-inner)" }),
            React.createElement("esl-scrollbar", { class: type.is + '-h-scroll', target: "::next(.uip-plugin-inner)", horizontal: true }),
            this.$inner));
    }
    connectedCallback() {
        super.connectedCallback();
        this.appendChild(this.$container);
    }
    disconnectedCallback() {
        this.$container.remove();
        super.disconnectedCallback();
    }
    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName === 'resizable' && newVal === null)
            this.clearInlineSize();
        if (attrName === 'dir')
            this.updateDir();
    }
    update(e) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const isolated = ((_a = this.model.activeSnippet) === null || _a === void 0 ? void 0 : _a.isolated) || false;
            if (!isolated)
                return this.writeContent();
            if (!e || this.forceUpdate || e.force)
                return this.writeIsolatedContent();
            return this.updateIsolatedContent();
        });
    }
    /** Writes the content directly to the inner area (non-isolated frame) */
    writeContent() {
        return __awaiter(this, void 0, void 0, function* () {
            this.$inner.innerHTML = UIPHTMLRenderingPreprocessors.preprocess(this.model.html);
            this.stopIframeResizeLoop();
            return promisifyNextRender();
        });
    }
    updateIsolatedContent() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.$iframe.contentWindow)
                return;
            const $document = (_a = this.$iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document;
            const $root = ($document === null || $document === void 0 ? void 0 : $document.querySelector('[uip-content-root]')) || ($document === null || $document === void 0 ? void 0 : $document.body);
            if ($root) {
                $root.innerHTML = UIPHTMLRenderingPreprocessors.preprocess(this.model.html);
                return promisifyNextRender();
            }
        });
    }
    /** Writes the content to the iframe inner (isolated frame) */
    writeIsolatedContent() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.$iframe.parentElement !== this.$inner) {
                this.$inner.innerHTML = '';
                this.$inner.appendChild(this.$iframe);
            }
            this.stopIframeResizeLoop();
            this.$iframe.src = 'about:blank';
            this.$iframe.hidden = true;
            this.startIframeResizeLoop();
            yield promisifyEvent(this.$iframe, 'load');
            yield promisifyTimeout(this.refreshDelay);
        });
    }
    /** Start and do a resize sync-loop iteration. Recall itself on the next frame. */
    startIframeResizeLoop() {
        var _a;
        if (!this.resizeLoop)
            return;
        // Prevents multiple loops
        if (this._iframeResizeRAF)
            cancelAnimationFrame(this._iframeResizeRAF);
        // Addition loop fallback for iframe removal
        if (this.$iframe.parentElement !== this.$inner)
            return;
        const $document = (_a = this.$iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document;
        const $root = ($document === null || $document === void 0 ? void 0 : $document.querySelector('[uip-content-root]')) || ($document === null || $document === void 0 ? void 0 : $document.body);
        if (!$root)
            return;
        // Reflect iframe height with inner content
        this.$iframe.style.height = `${$root.scrollHeight}px`;
        this._iframeResizeRAF = requestAnimationFrame(this.startIframeResizeLoop.bind(this));
    }
    /** Stop resize loop iterations created by `startIframeResizeLoop` */
    stopIframeResizeLoop() {
        if (this._iframeResizeRAF)
            cancelAnimationFrame(this._iframeResizeRAF);
        this._iframeResizeRAF = 0;
    }
    /** Resets element both inline height and width properties */
    clearInlineSize() {
        this.$inner.style.removeProperty('height');
        this.$inner.style.removeProperty('width');
    }
    updateDir() {
        const isChanged = this.dir !== this.$inner.dir;
        this.$inner.dir = this.dir;
        isChanged && this.$$fire('uip:dirchange');
    }
    _onIframeLoad() {
        var _a, _b, _c, _d, _e;
        if (!this.$iframe.contentWindow)
            return;
        const title = ((_a = this.model.activeSnippet) === null || _a === void 0 ? void 0 : _a.label) || 'UI Playground';
        const script = UIPJSRenderingPreprocessors.preprocess(this.model.js);
        const content = UIPHTMLRenderingPreprocessors.preprocess(this.model.html);
        const isolatedTemplate = ((_b = this.model.activeSnippet) === null || _b === void 0 ? void 0 : _b.isolatedTemplate) || 'default';
        const html = UIPRenderingTemplatesService.render(isolatedTemplate, { title, content, script });
        (_c = this.$iframe.contentWindow) === null || _c === void 0 ? void 0 : _c.document.close();
        (_d = this.$iframe.contentWindow) === null || _d === void 0 ? void 0 : _d.document.write(html);
        (_e = this.$iframe.contentWindow) === null || _e === void 0 ? void 0 : _e.document.close();
        this.$iframe.hidden = false;
        this.$iframe.title = title;
    }
    /** Updates preview content from the model state changes */
    _onRootStateChange(e) {
        return __awaiter(this, void 0, void 0, function* () {
            this.$container.style.transition = 'none';
            this.$container.style.minHeight = `${this.$inner.offsetHeight}px`;
            this.$$attr('loading', true);
            yield this.update(e);
            this.$$attr('loading', false);
            this.$container.style.removeProperty('transition');
            this.$container.style.removeProperty('min-height');
        });
    }
    /** Handles visibility change of the preview are to limit resize sync-loops to the active preview area */
    _onIntersects(e) {
        e.isIntersecting ? this.startIframeResizeLoop() : this.stopIframeResizeLoop();
    }
}
UIPPreview.is = 'uip-preview';
UIPPreview.observedAttributes = ['dir', 'resizable'];
__decorate([
    prop(true)
], UIPPreview.prototype, "resizeLoop", void 0);
__decorate([
    attr({ parser: parseBoolean, serializer: toBooleanAttribute })
], UIPPreview.prototype, "forceUpdate", void 0);
__decorate([
    attr({ defaultValue: 150, parser: parseInt })
], UIPPreview.prototype, "refreshDelay", void 0);
__decorate([
    memoize()
], UIPPreview.prototype, "$inner", null);
__decorate([
    memoize()
], UIPPreview.prototype, "$iframe", null);
__decorate([
    memoize()
], UIPPreview.prototype, "$container", null);
__decorate([
    listen({
        event: 'load',
        target: ($this) => $this.$iframe
    })
], UIPPreview.prototype, "_onIframeLoad", null);
__decorate([
    listen({ event: 'uip:change', target: ($this) => $this.$root })
], UIPPreview.prototype, "_onRootStateChange", null);
__decorate([
    listen({
        event: 'intersects',
        target: (preview) => ESLIntersectionTarget.for(preview.$container, { threshold: 0.01 }),
    })
], UIPPreview.prototype, "_onIntersects", null);
